import type { IProjectMetadata } from './types';
import type { Options as UrlManagerOptions } from '@readme/backend/UrlManager';
import type { MouseEvent } from 'react';

import loadable from '@loadable/component';
import { useEffect, useMemo, useRef } from 'react';

import useAskAIState from '@core/hooks/useAskAIState';
import useReadmeApi from '@core/hooks/useReadmeApi';
import { useSuperHubStore } from '@core/store';
import classy from '@core/utils/classy';

/**
 * Code-splitting the legacy AppSearch and SuperHubSearch components to avoid
 * loading unnecessary JS on the Hub.
 */
const AppSearch = loadable(() => import('./index'), { ssr: true });
const SuperHubSearch = loadable(() => import('@Hub/SuperHubSearch'), {
  ssr: true,
});

interface Props {
  appId: string;
  indexName: string;
  isModalOpen: boolean;
  projectsMeta: IProjectMetadata[];
  searchApiKey: string;
  toggleOpen: () => void;
  urlManagerOpts: UrlManagerOptions;
}

const SearchModal = ({
  appId,
  indexName,
  isModalOpen,
  projectsMeta,
  searchApiKey,
  toggleOpen,
  urlManagerOpts,
}: Props) => {
  const [isSuperHub, getApiBaseUrl, currentBranch] = useSuperHubStore(s => [
    s.isSuperHub,
    s.getApiBaseUrl,
    s.currentBranch,
  ]);

  // In SuperHub, custom page visibility is managed by Gitto rather than the
  // project.modules.custompages flag, which may be stale. Use the lightweight
  // sidebar endpoint (which returns only metadata, not page content) to check
  // if any visible custom pages exist on the current branch.
  const { data: customPagesSidebar } = useReadmeApi<{ pages: { hidden: boolean }[] }[]>(
    isSuperHub ? `/branches/${currentBranch.name}/sidebar?page_type=custom_page` : null,
    { apiBaseUrl: getApiBaseUrl({ branch: null }) },
  );

  const customPagesVisible = customPagesSidebar?.some(category => category.pages.some(page => !page.hidden));

  // Override the stale modules.custompages flag with live data from Gitto
  const resolvedProjectsMeta = useMemo(() => {
    if (typeof customPagesVisible !== 'boolean' || !Array.isArray(projectsMeta)) return projectsMeta;
    return projectsMeta.map(meta => ({
      ...meta,
      modules: { ...meta.modules, custompages: customPagesVisible },
    }));
  }, [projectsMeta, customPagesVisible]);

  // Old experience replaces the search modal with SearchBert, so Cmd+K should not toggle this modal
  const { renderMode } = useAskAIState();
  const owlbotOldExperienceEnabled = renderMode === 'owlbot';

  useEffect(() => {
    const onKeydown = e => {
      if (e.key === 'Escape') {
        // When a user presses 'esc', close the modal if it's active
        if (isModalOpen) toggleOpen();
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key.length === 1 &&
        e.key.toLowerCase() === 'k' &&
        !owlbotOldExperienceEnabled
      ) {
        // When a user presses 'cmd-k' or 'ctrl-k', toggle the modal open or closed
        e.preventDefault();
        toggleOpen();
      }
    };

    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    window?.addEventListener('keydown', onKeydown);
    return () => window?.removeEventListener('keydown', onKeydown);
  }, [isModalOpen, owlbotOldExperienceEnabled, toggleOpen]);

  const id = 'AppSearch';

  // Tracks whether the mousedown that started the current click landed on the backdrop.
  // Prevents closing the modal when a drag-select that started inside content (e.g. the
  // search input) ends with mouseup on the backdrop - the browser fires `click` on the
  // common ancestor (the backdrop), which would otherwise trigger close.
  const mouseDownOnBackdrop = useRef(false);

  return (
    <div
      className={`hub-search-results--reactApp ${isModalOpen ? 'hub-search-results-active' : ''}`}
      id="hub-search-results"
    >
      <div className="hub-container">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div
          className={classy('modal-backdrop', 'rm-SearchModal', isModalOpen && 'show-modal')}
          onClick={(event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const wasMouseDownOnBackdrop = mouseDownOnBackdrop.current;
            mouseDownOnBackdrop.current = false;
            if (!target.closest(`#${id}`) && wasMouseDownOnBackdrop) {
              toggleOpen();
            }
          }}
          onMouseDown={(event: MouseEvent) => {
            const target = event.target as HTMLElement;
            mouseDownOnBackdrop.current = !target.closest(`#${id}`);
          }}
          role="button"
          tabIndex={0}
        >
          {/* SuperHub search uses Mongo Atlas instead of Algolia so we have different search subcomponents */}
          {isSuperHub ? (
            isModalOpen ? (
              <SuperHubSearch
                id={id}
                isModalOpen={isModalOpen}
                projectsMeta={resolvedProjectsMeta}
                toggleModal={toggleOpen}
              />
            ) : null
          ) : (
            !!searchApiKey && (
              <AppSearch
                appId={appId}
                id={id}
                indexName={indexName}
                isModalOpen={isModalOpen}
                projectsMeta={projectsMeta}
                searchApiKey={searchApiKey}
                urlManagerOpts={urlManagerOpts}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
